import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Shield, Stethoscope, Award, Calendar, ShieldAlert, Clock, TrendingUp, ChevronRight, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => {
  const token = localStorage.getItem('staff_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : null;
};

const TYPE_HEX = {
  sickness: '#F59E0B', absence: '#F97316',
  warning_verbal: '#F59E0B', warning_written: '#F43F5E', warning_final: '#F43F5E',
  disciplinary_invite: '#F43F5E', investigation_invite: '#8A2BE2', absence_review_invite: '#00E5FF',
  outcome_dismissal: '#F43F5E', outcome_no_action: '#10B981', outcome_final_warning: '#F43F5E',
  outcome_action_plan: '#00E5FF', commendation: '#10B981', note: '#94A3B8',
};

/**
 * Slide-in drawer showing a comprehensive HR profile for a staff member.
 * Includes active warnings, absence history, ack rate, all notices.
 */
export const StaffHRProfile = ({ username, onClose, onOpenNotice }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview'); // overview | warnings | absences | outcomes

  useEffect(() => {
    if (!username) return;
    setLoading(true);
    const load = async () => {
      const h = getAuthHeaders(); if (!h) return;
      try {
        const r = await axios.get(`${BACKEND_URL}/api/hr-notices/staff/${username}/profile`, { headers: h });
        setData(r.data);
      } catch {}
      setLoading(false);
    };
    load();
  }, [username]);

  if (!username) return null;

  const staff = data?.staff || {};
  const stats = data?.stats || {};

  const StatCard = ({ label, value, hex, icon: Icon }) => (
    <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="w-3 h-3" style={{ color: hex }} />
        <p className="text-[9px] tracking-[0.14em] uppercase font-bold" style={{ color: `${hex}CC` }}>{label}</p>
      </div>
      <p className="text-white text-xl font-light tabular-nums leading-none">{value}</p>
    </div>
  );

  const NoticeRow = ({ n }) => {
    const hex = TYPE_HEX[n.type] || '#94A3B8';
    return (
      <button onClick={() => onOpenNotice && onOpenNotice(n)}
        data-testid={`profile-notice-${n.id}`}
        className="w-full text-left flex items-center gap-2.5 p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.15] transition-all">
        <span className="w-1 h-8 rounded-full" style={{ background: hex }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-white text-[12.5px] font-medium truncate">{n.title}</p>
            {n.is_active !== undefined && (
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[8.5px] font-bold uppercase tracking-wider border ${n.is_active ? 'bg-rose-500/10 border-rose-500/40 text-rose-300' : 'bg-white/[0.03] border-white/[0.08] text-white/40'}`}>
                {n.is_active ? `${n.days_remaining}d left` : 'Expired'}
              </span>
            )}
          </div>
          <p className="text-white/45 text-[10px] mt-0.5">
            {n.type_label} · {new Date(n.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
        </div>
        <ChevronRight className="w-3.5 h-3.5 text-white/25" />
      </button>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="profile-backdrop" />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 z-[101] w-full max-w-xl bg-gradient-to-br from-[#0A0A14] via-[#0D0B1F] to-[#0A0A14] border-l border-cyan-500/20 shadow-[0_0_60px_-15px_rgba(0,229,255,0.35)] overflow-hidden flex flex-col"
        style={{ animation: 'slideInRight 0.25s ease-out' }} data-testid="staff-hr-profile-drawer">
        <style>{`@keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>

        {/* Header */}
        <div className="relative overflow-hidden border-b border-white/[0.06] p-5">
          <div className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full bg-cyan-500/[0.10] blur-[100px]" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-violet-500/[0.10] blur-[100px]" />
          <div className="relative flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl border border-white/[0.10] bg-gradient-to-br from-cyan-500/30 to-violet-500/20 flex items-center justify-center flex-shrink-0">
              {staff.avatar_url ? (
                <img src={staff.avatar_url} alt="" className="w-full h-full rounded-2xl object-cover" />
              ) : (
                <span className="text-white text-lg font-semibold">{(staff.display_name || username || '?').charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-[0.24em] uppercase text-cyan-300/70 font-medium">Staff · HR profile</p>
              <h2 className="text-2xl font-semibold text-white tracking-tight mt-0.5 truncate">{staff.display_name || username}</h2>
              <p className="text-white/50 text-xs mt-0.5">@{staff.username || username} · {staff.role || '—'} {staff.job_title ? `· ${staff.job_title}` : ''}</p>
            </div>
            <button onClick={onClose} data-testid="profile-close-btn"
              className="w-8 h-8 rounded-lg text-white/40 hover:text-white hover:bg-white/[0.06] flex items-center justify-center flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Alerts */}
          {stats.active_warnings > 0 && (
            <div className="relative mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-500/[0.10] border border-rose-500/30 text-rose-300 text-[11px] font-medium">
              <ShieldAlert className="w-3 h-3" />
              {stats.active_warnings} active warning{stats.active_warnings === 1 ? '' : 's'}
            </div>
          )}
        </div>

        {/* Stats grid */}
        {loading ? (
          <div className="text-center py-14 text-white/40 text-sm">Loading profile…</div>
        ) : (
        <div className="flex-1 overflow-y-auto jewel-scroll">
          <div className="p-5 pb-3 grid grid-cols-2 md:grid-cols-4 gap-2">
            <StatCard label="Total notices" value={stats.total_notices || 0} hex="#00E5FF" icon={User} />
            <StatCard label="Active warnings" value={stats.active_warnings || 0} hex="#F43F5E" icon={ShieldAlert} />
            <StatCard label="Absences · 12mo" value={stats.total_absences_12mo || 0} hex="#F97316" icon={Stethoscope} />
            <StatCard label="Ack rate" value={`${stats.ack_rate || 100}%`} hex="#10B981" icon={TrendingUp} />
          </div>

          {/* Tabs */}
          <div className="px-5">
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.02] border border-white/[0.06]">
              {[
                { id: 'overview', label: 'Recent', count: data?.recent?.length || 0 },
                { id: 'warnings', label: 'Warnings', count: data?.warnings?.length || 0 },
                { id: 'absences', label: 'Absences', count: data?.absences?.length || 0 },
                { id: 'outcomes', label: 'Outcomes', count: data?.outcomes?.length || 0 },
              ].map(t => {
                const a = tab === t.id;
                return (
                  <button key={t.id} onClick={() => setTab(t.id)}
                    data-testid={`profile-tab-${t.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10.5px] font-medium transition-all ${
                      a ? 'text-white bg-white/[0.06] border border-white/20' : 'text-white/50 hover:text-white border border-transparent'
                    }`}>
                    {t.label}
                    <span className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold ${a ? 'bg-cyan-500/25 text-cyan-200' : 'bg-white/[0.04] text-white/45'}`}>{t.count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab content */}
          <div className="p-5 pt-3 space-y-1.5">
            {tab === 'overview' && (data?.recent || []).map(n => <NoticeRow key={n.id} n={n} />)}
            {tab === 'warnings' && (data?.warnings || []).map(n => <NoticeRow key={n.id} n={n} />)}
            {tab === 'absences' && (data?.absences || []).map(n => <NoticeRow key={n.id} n={n} />)}
            {tab === 'outcomes' && (data?.outcomes || []).map(n => <NoticeRow key={n.id} n={n} />)}
            {tab === 'overview' && (data?.recent || []).length === 0 && <p className="text-white/30 text-xs italic text-center py-6">No notices yet</p>}
            {tab === 'warnings' && (data?.warnings || []).length === 0 && <p className="text-white/30 text-xs italic text-center py-6">No warnings on record</p>}
            {tab === 'absences' && (data?.absences || []).length === 0 && <p className="text-white/30 text-xs italic text-center py-6">No absences</p>}
            {tab === 'outcomes' && (data?.outcomes || []).length === 0 && <p className="text-white/30 text-xs italic text-center py-6">No outcomes</p>}
          </div>
        </div>
        )}
      </div>
    </>
  );
};

export default StaffHRProfile;
